import React from 'react';
import './PrivacyPolicy.css';

const PrivacyPolicy = () => {
  return (
    <div className="privacy-policy-page">
      <div className="privacy-container">
        <h1>Privacy Policy</h1>
        <p className="last-updated">Last Updated: {new Date().toLocaleDateString('en-AU', { year: 'numeric', month: 'long', day: 'numeric' })}</p>

        <section className="privacy-section">
          <h2>1. Introduction</h2>
          <p>
            Data Migration Discovery Framework ("DDMF", "we", "us", or "our") operates the website at DDMF.app (the "Service").
            We are committed to protecting your privacy and complying with the Australian Privacy Principles (APPs) under the
            Privacy Act 1988 (Cth).
          </p>
          <p>
            This Privacy Policy explains how we collect, use, disclose, and safeguard your personal information when you use
            our Service. By accessing or using our Service, you acknowledge that you have read, understood, and agree to be
            bound by this Privacy Policy.
          </p>
          <p className="business-location">
            <strong>Business Location:</strong> Melbourne, Victoria, Australia
          </p>
        </section>

        <section className="privacy-section">
          <h2>2. Information We Collect</h2>

          <h3>2.1 Personal Information</h3>
          <p>We collect the following types of personal information when you use our Service:</p>
          <ul>
            <li><strong>Account Information:</strong> Name and email address when you register for an account</li>
            <li><strong>Authentication Data:</strong> Password (stored in encrypted form) or OAuth credentials from third-party providers (Google, GitHub)</li>
            <li><strong>Project Discovery Data:</strong> Your responses to discovery questionnaires and project-related information</li>
            <li><strong>Payment Information:</strong> Payment details processed securely through our third-party payment processor (Stripe)</li>
          </ul>

          <h3>2.2 Automatically Collected Information</h3>
          <p>When you access our Service, we may automatically collect:</p>
          <ul>
            <li>Device information (browser type, operating system)</li>
            <li>Usage data (pages visited, time spent on pages)</li>
            <li>IP address and location data</li>
          </ul>

          <h3>2.3 Local Storage</h3>
          <p>
            Our Service uses browser local storage (not cookies) to maintain your authentication session. When you log in,
            we store a JWT (JSON Web Token) authentication token in your browser's local storage. This token is used to
            keep you logged in as you navigate the Service. You can clear this data at any time through your browser settings,
            though doing so will log you out of the Service.
          </p>
        </section>

        <section className="privacy-section">
          <h2>3. How We Use Your Information</h2>
          <p>We use your personal information for the following purposes:</p>

          <h3>3.1 Service Delivery</h3>
          <ul>
            <li>To create and manage your user account</li>
            <li>To provide access to the discovery framework and generate project plans</li>
            <li>To process payments and maintain billing records</li>
            <li>To provide customer support and respond to your inquiries</li>
          </ul>

          <h3>3.2 Communication</h3>
          <ul>
            <li>To send you service-related updates and notifications</li>
            <li>To contact you regarding your project discovery and implementation</li>
            <li>To send you marketing communications about our services (with your consent)</li>
            <li>To respond to leads who sign up for project discovery</li>
          </ul>

          <h3>3.3 Service Improvement</h3>
          <ul>
            <li>To analyze usage patterns and improve our Service</li>
            <li>To develop new features and functionality</li>
            <li>To ensure the security and integrity of our platform</li>
          </ul>
        </section>

        <section className="privacy-section">
          <h2>4. Email Communications and Marketing</h2>
          <p className="highlight-box">
            <strong>Our Commitment to You:</strong> We collect your email address to provide our services and communicate
            with you about your projects. We may also use your email to reach out to leads who sign up for project discovery
            and to send you information about our services.
          </p>
          <p className="highlight-box">
            <strong>We Will Never:</strong>
          </p>
          <ul className="no-share-list">
            <li>Sell your email address to third parties</li>
            <li>Rent or lease your contact information</li>
            <li>Share your email with other companies for their marketing purposes</li>
            <li>Use your email for any purpose other than providing our Service and legitimate business communications</li>
          </ul>
          <p>
            You have the right to opt out of marketing communications at any time by clicking the "unsubscribe" link in any
            marketing email we send you, or by contacting us directly. Please note that you will continue to receive
            essential service-related communications even if you opt out of marketing emails.
          </p>
        </section>

        <section className="privacy-section">
          <h2>5. Data Security and Storage</h2>

          <h3>5.1 Security Measures</h3>
          <p>
            We implement industry-standard security measures to protect your personal information from unauthorized access,
            disclosure, alteration, or destruction. These measures include:
          </p>
          <ul>
            <li>Secure password hashing using bcrypt encryption</li>
            <li>JWT (JSON Web Token) authentication for secure session management</li>
            <li>HTTPS encryption for data transmission</li>
            <li>Access controls limiting data access to authorized personnel only</li>
            <li>Regular security assessments and updates</li>
          </ul>

          <h3>5.2 Data Storage</h3>
          <p>
            Your personal information and discovery responses are stored securely in our database with appropriate access
            controls. Only authorized personnel can access your data for legitimate business purposes such as service
            delivery, technical support, and system maintenance.
          </p>
          <p>
            While we implement robust security measures, please be aware that no method of electronic storage or transmission
            over the Internet is 100% secure. We cannot guarantee absolute security but are committed to protecting your
            information to the best of our ability.
          </p>

          <h3>5.3 Data Retention</h3>
          <p>
            We retain your personal information for as long as your account is active or as needed to provide you with our
            Service. If you wish to delete your account or request that we no longer use your information, please contact us.
            We will retain and use your information as necessary to comply with legal obligations, resolve disputes, and
            enforce our agreements.
          </p>
        </section>

        <section className="privacy-section">
          <h2>6. Information Sharing and Disclosure</h2>
          <p>We do not sell, trade, or rent your personal information to third parties. We may share your information only in the following limited circumstances:</p>

          <h3>6.1 Service Providers</h3>
          <p>
            We may share your information with trusted third-party service providers who assist us in operating our Service,
            such as:
          </p>
          <ul>
            <li>Payment processors (Stripe) for handling transactions</li>
            <li>Cloud hosting providers for data storage and infrastructure</li>
            <li>Email service providers for sending communications</li>
          </ul>
          <p>
            These service providers are contractually obligated to protect your information and use it only for the purposes
            we specify.
          </p>

          <h3>6.2 Legal Requirements</h3>
          <p>We may disclose your information if required to do so by law or in response to:</p>
          <ul>
            <li>Valid legal processes (subpoenas, court orders)</li>
            <li>Requests from government authorities</li>
            <li>Protection of our rights, property, or safety</li>
            <li>Protection of the rights, property, or safety of our users or the public</li>
          </ul>

          <h3>6.3 Business Transfers</h3>
          <p>
            In the event of a merger, acquisition, or sale of assets, your personal information may be transferred to the
            acquiring entity. We will notify you via email and/or a prominent notice on our Service of any such change in
            ownership or use of your personal information.
          </p>
        </section>

        <section className="privacy-section">
          <h2>7. Your Privacy Rights (Australian Privacy Principles)</h2>
          <p>Under Australian privacy law, you have the following rights regarding your personal information:</p>

          <h3>7.1 Access and Correction</h3>
          <p>
            You have the right to access the personal information we hold about you and to request corrections if it is
            inaccurate, incomplete, or out of date.
          </p>

          <h3>7.2 Data Portability</h3>
          <p>
            You have the right to request a copy of your personal information in a structured, commonly used, and
            machine-readable format.
          </p>

          <h3>7.3 Deletion</h3>
          <p>
            You have the right to request deletion of your personal information, subject to certain legal exceptions
            (e.g., where we are required to retain information for legal or regulatory purposes).
          </p>

          <h3>7.4 Withdrawal of Consent</h3>
          <p>
            Where we process your personal information based on your consent, you have the right to withdraw that consent
            at any time.
          </p>

          <h3>7.5 Complaints</h3>
          <p>
            If you believe we have breached the Australian Privacy Principles, you have the right to lodge a complaint with
            us. If you are not satisfied with our response, you may lodge a complaint with the Office of the Australian
            Information Commissioner (OAIC) at <a href="https://www.oaic.gov.au" target="_blank" rel="noopener noreferrer">www.oaic.gov.au</a>.
          </p>

          <p className="contact-rights">
            To exercise any of these rights, please contact us using the details provided in the "Contact Us" section below.
          </p>
        </section>

        <section className="privacy-section">
          <h2>8. Third-Party Links</h2>
          <p>
            Our Service may contain links to third-party websites or services (such as OAuth providers like Google and GitHub).
            We are not responsible for the privacy practices of these third parties. We encourage you to review the privacy
            policies of any third-party sites you visit.
          </p>
        </section>

        <section className="privacy-section">
          <h2>9. Children's Privacy</h2>
          <p>
            Our Service is not intended for individuals under the age of 18. We do not knowingly collect personal information
            from children. If we become aware that we have collected personal information from a child without parental consent,
            we will take steps to delete that information promptly.
          </p>
        </section>

        <section className="privacy-section">
          <h2>10. International Data Transfers</h2>
          <p>
            Your information may be transferred to and processed in countries other than Australia. We ensure that such
            transfers comply with applicable privacy laws and that appropriate safeguards are in place to protect your
            information.
          </p>
        </section>

        <section className="privacy-section">
          <h2>11. Changes to This Privacy Policy</h2>
          <p>
            We may update this Privacy Policy from time to time to reflect changes in our practices, technology, legal
            requirements, or other factors. We will notify you of any material changes by:
          </p>
          <ul>
            <li>Posting the updated Privacy Policy on this page</li>
            <li>Updating the "Last Updated" date at the top of this policy</li>
            <li>Sending you an email notification (for significant changes)</li>
          </ul>
          <p>
            Your continued use of the Service after any changes to this Privacy Policy constitutes your acceptance of the
            updated policy.
          </p>
        </section>

        <section className="privacy-section">
          <h2>12. Contact Us</h2>
          <p>
            If you have any questions, concerns, or requests regarding this Privacy Policy or our privacy practices, please
            contact us:
          </p>
          <div className="contact-info">
            <p><strong>Data Migration Discovery Framework (DDMF)</strong></p>
            <p>Location: Melbourne, Victoria, Australia</p>
            <p>Website: <a href="https://ddmf.app">DDMF.app</a></p>
          </div>
          <p>
            We will respond to your inquiry within a reasonable timeframe, typically within 30 days.
          </p>
        </section>

        <section className="privacy-section">
          <h2>13. Acknowledgment and Consent</h2>
          <p>
            By using our Service, you acknowledge that you have read and understood this Privacy Policy and consent to the
            collection, use, and disclosure of your personal information as described herein.
          </p>
        </section>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
